import ExcelJS from "exceljs";
import QRCode from "qrcode-svg";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export const getWritePermission = (pagePermissionId) => {
  const permissionValues1 =
    JSON.parse(localStorage.getItem("rolePermissions")) || [];
  let canWriteValue = null;

  // Prefer the caller's own page permissionId. Only fall back to the shared
  // localStorage "permissionId" (which can be stale on refresh/back navigation
  // and caused Add/Update buttons to sometimes hide) when no id is provided.
  const storedPermissions =
    pagePermissionId != null
      ? pagePermissionId
      : parseInt(localStorage.getItem("permissionId"), 10);

  const matchingPermission = (
    Array.isArray(permissionValues1) ? permissionValues1 : []
  ).find((mapping) => mapping.permissionId === storedPermissions);

  if (matchingPermission) {
    canWriteValue = matchingPermission.canWrite;
  } else {
    console.log(`No match found for permission ID ${storedPermissions}`);
  }
  return canWriteValue;
};

export const downloadExcel = async (vehicleDetails) => {
  // Create a new workbook and add a worksheet
  const currentDate = new Date();
  const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
  const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
  const cumulativeHeadingReport = `Vehicle Details`;
  const downloadDateTime = `Vehicle Report Generated on: ${formattedDate}, ${formattedTime}`;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Vehicle Details");

  // Define styles
  const boldFont = { bold: true };

  // Add the downloadDateTime to the correct row and columns, and ensure formatting
  worksheet.addRow(["", "", downloadDateTime, ""]);
  worksheet.mergeCells(1, 2, 1, 9); // Merge cells for downloadDateTime from column 2 to 9
  worksheet.getCell(1, 2).value = downloadDateTime;
  worksheet.getCell(1, 2).font = boldFont; // Set font style
  worksheet.getCell(1, 2).alignment = { horizontal: "center" };

  // Add "Vehicle Details" to the correct row and columns, and ensure formatting
  worksheet.addRow(["", "", "Vehicle Details", ""]);
  worksheet.mergeCells(2, 2, 2, 9); // Merge cells for "Vehicle Details"
  worksheet.getCell(2, 2).value = "Vehicle Details";
  worksheet.getCell(2, 2).font = boldFont;
  worksheet.getCell(2, 2).alignment = { horizontal: "center" };

  // Add an empty row for spacing
  worksheet.addRow([""]);

  // Add table header row with bold font
  const tableHeaderRow = worksheet.addRow([
    "S.No",
    "Vehicle Number",
    "Vehicle UID",
    "Vehicle Type",
    "Vehicle Capacity",
    "Corporation",
    "Zone",
    "Circle",
    "Ward",
    "Fuel Type",
    "Collection Type",
    "Vehicle Status",
    "Owner Type",
    "Vehicle UID",
    "Driver Name",
    "Driver Number",
    "Primary Deployment Incharge Name",
    "Primary Deployment Incharge Number",
    "Secondary Deployment Incharge Name",
    "Secondary Deployment Incharge Number",
    "Transfer Station",
    "Vehicle Brand Name",
    "Contractor Name",
    "RFID",
    "Remarks",
    "Status",
  ]);

  tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = boldFont;
    cell.alignment = { horizontal: "left" }; // Left-align the cell content
    if (colNumber >= 1 && colNumber <= 28) {
      // Apply background color to specific cells
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F6B1B1" }, // Light red background color
      };
    }
  });

  // Add data rows from vehicleDetails
  vehicleDetails?.forEach((item, index) => {
    worksheet.addRow([
      index + 1, // S.No
      item.vehicleNumber,
      item.vehicleUuid,
      item.vehicleTypeName,
      item.vehicleCapacityName,
      item.corporationName || "N/A",
      item.zoneName || "N/A",
      item.circleName || "N/A",
      item.wardName || "N/A",
      item.fuelTypeName || "N/A",
      item.collectionTypeName || "N/A",
      item.vehicleStatusName || "N/A",
      item.ownerTypeName || "N/A",
      item.vehicleUuid || "N/A",
      item.driverName || "N/A",
      item.driverNumber || "N/A",
      item.primaryDeploymentInchargeName || "N/A",
      item.primaryDeploymentInchargeNumber || "N/A",
      item.secondaryDeploymentInchargeName || "N/A",
      item.secondaryDeploymentInchargeNumber || "N/A",
      item.transferStationName || "N/A",
      item.vehicleBrandName || "N/A",
      item.contractorName || "N/A",
      item.rfid || "N/A",
      item.remark || "N/A",
      item.isActive ? "Active" : "InActive", // Status
    ]).eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.alignment = { horizontal: "left" };
      if (colNumber === 28) {
        cell.alignment = { horizontal: "left" }; // Center align the status column
      }
    });
  });

  worksheet.columns = [
    { width: 5 },     // S.No
    { width: 25 },    // Vehicle Number
    { width: 20 },    // Vehicle UID
    { width: 20 },    // Vehicle Type
    { width: 20 },    // Vehicle Capacity
    { width: 25 },    // Corporation
    { width: 20 },    // Zone
    { width: 20 },  // Circle
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 30 },
    { width: 30 },
    { width: 30 },
    { width: 40 },
    { width: 40 },
    { width: 40 },
    { width: 40 },
    { width: 30 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
  ];



  // Save the workbook to a file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "VehicleDetails.xlsx"; // The filename for the downloaded Excel
  a.click();
  URL.revokeObjectURL(url); // Clean up the blob URL
};

const options = { day: "numeric", month: "numeric", year: "numeric" };

const generationTime = ` ${new Date()
  .toLocaleString("en-GB", options)
  .replace(/\//g, "/")}`;

const getCorporationColor = (corporationName) => {
  const name = (corporationName || "").toUpperCase();
  if (name.includes("GHMC")) return [70, 160, 73];
  if (name.includes("MMC")) return [189, 192, 0];
  if (name.includes("CMC")) return [0, 112, 192];
  return [120, 120, 120];
};

const getCollectionRoman = (collectionTypeId) => {
  const map = { 1: "I", 2: "II", 3: "III" };
  return map[collectionTypeId] || "";
};

const addQROverlay = (pdf, vehicle, initialX, initialY, scaledWidth) => {
  const romanNumeral = getCollectionRoman(vehicle.collectionTypeId);
  const [r, g, b] = getCorporationColor(vehicle.corporationName);

  if (!romanNumeral) return;

  // Measure roman numeral width with serif bold font
  pdf.setFontSize(14);
  pdf.setFont("times", "bold");
  const romanWidth = pdf.getTextWidth(romanNumeral);

  const dotRadius = 3.5;
  const padX = 3.5;
  const gap = 2.5;
  const pillW = padX + romanWidth + gap + dotRadius * 2 + padX;
  const pillH = 10;
  const pillR = pillH / 2;

  const pillX = initialX + scaledWidth - pillW;
  const pillY = initialY - pillH - 3;

  // White pill background with light grey border
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.4);
  pdf.roundedRect(pillX, pillY, pillW, pillH, pillR, pillR, "FD");

  // Roman numeral in serif bold
  pdf.setFontSize(14);
  pdf.setFont("times", "bold");
  pdf.setTextColor(20, 20, 20);
  pdf.text(romanNumeral, pillX + padX, pillY + pillH * 0.67);

  // Colored dot
  const dotCX = pillX + padX + romanWidth + gap + dotRadius;
  const dotCY = pillY + pillH / 2;
  pdf.setFillColor(r, g, b);
  pdf.setDrawColor(r, g, b);
  pdf.circle(dotCX, dotCY, dotRadius, "F");

  // Reset
  pdf.setDrawColor(0, 0, 0);
  pdf.setTextColor(0, 0, 0);
};

const getQRCodeVehicleDetails = (vehicle) => {
  const isPrimaryVehicle = Number(vehicle.collectionTypeId) === 1;
  const details = [
    ["CORPORATION", vehicle.corporationName],
    ["ZONE", vehicle.zoneName],
    ["CIRCLE", vehicle.circleName],
  ];

  if (isPrimaryVehicle) {
    details.push(["WARD", vehicle.wardName]);
  }

  details.push(
    ["OWNER TYPE", vehicle.ownerTypeName],
    ["VEHICLE TYPE", vehicle.vehicleTypeName],
    ["VEH. REG. NO", vehicle.vehicleNumber]
  );

  details.push(["UID", vehicle.vehicleUuid]);

  if (!isPrimaryVehicle) {
    details.push(["VEHICLE CAPACITY", vehicle.vehicleCapacityName]);
  }

  return details;
};

const addQRCodeVehicleDetails = (pdf, vehicle, startY) => {
  const details = getQRCodeVehicleDetails(vehicle);
  const margin = 15;
  const lineH = 5.5;
  const pageW = pdf.internal.pageSize.getWidth();

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "bold");

  let maxLabelWidth = 0;
  details.forEach(([label]) => {
    const width = pdf.getTextWidth(label);
    if (width > maxLabelWidth) maxLabelWidth = width;
  });

  pdf.setFont("helvetica", "normal");
  let maxValueWidth = 0;
  details.forEach(([, value]) => {
    const width = pdf.getTextWidth(value || "-");
    if (width > maxValueWidth) maxValueWidth = width;
  });

  const detailWidth = Math.min(
    maxLabelWidth + 5 + maxValueWidth,
    pageW - margin * 2
  );
  const labelX = (pageW - detailWidth) / 2;
  const valueX = labelX + maxLabelWidth + 5;
  const valueMaxWidth = pageW - valueX - labelX;
  let currentY = startY;

  details.forEach(([label, value]) => {
    pdf.setFont("helvetica", "bold");
    pdf.text(label, labelX, currentY);
    pdf.setFont("helvetica", "normal");
    pdf.text(":", labelX + maxLabelWidth + 2, currentY);

    const lines =
      label === "UID" ? [value || "-"] : pdf.splitTextToSize(value || "-", valueMaxWidth);
    pdf.text(lines, valueX, currentY);
    currentY += Math.max(lineH, lines.length * lineH);
  });
};

export const downloadQRCodeImage = (vehicle) => {
  try {
    const uuid = vehicle?.vehicleUuid;
    const qr = new QRCode({
      content: uuid,
      padding: 4,
      width: 256,
      height: 256,
      color: "#000000",
      background: "#ffffff",
      ecl: "M",
    });

    const svg = qr.svg();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = 256;
      canvas.height = 256;
      ctx.drawImage(img, 0, 0, 256, 256);

      const imgData = canvas.toDataURL("image/jpeg", 1.0);

      const pdf = new jsPDF("p", "mm", "a4");
      pdf.setFont("helvetica", "bold");

      const scaleFactor = 0.5;
      const scaledWidth = 256 * scaleFactor;
      const scaledHeight = 256 * scaleFactor;

      const initialX = (pdf.internal.pageSize.getWidth() - scaledWidth) / 2;
      const initialY = 25;

      // Add image to PDF
      pdf.addImage(
        imgData,
        "JPEG",
        initialX,
        initialY,
        scaledWidth,
        scaledHeight
      );

      addQROverlay(pdf, vehicle, initialX, initialY, scaledWidth);

      addQRCodeVehicleDetails(pdf, vehicle, initialY + scaledHeight + 10);

      // Generate PDF and initiate download
      pdf.save(`qr-code-${vehicle?.vehicleNumber}.pdf`);
    };

    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  } catch (error) {
    console.error("Error generating QR code:", error);
  }
};

export const generateQRCodeSVG = (vehicleUuid) => {
  try {
    const qr = new QRCode({
      content: vehicleUuid,
      width: 400,
      height: 400,
      color: "#000000",
    });
    return qr.svg();
  } catch (error) {
    console.error("Error generating QR code:", error);
    return "<svg></svg>";
  }
};


export const handleDownloadQRCode = async (vehicles) => {
  const zip = new JSZip();
  const qrSize = 256;
  const limitedVehicles = vehicles.slice(0, 1000);
  const promises = limitedVehicles.map((vehicle, index) => {
    return new Promise((resolve, reject) => {
      const qrContent = vehicle.vehicleUuid;

      // Create QR code
      const qr = new QRCode({
        content: qrContent || "",
        padding: 4,
        width: qrSize,
        height: qrSize,
        color: "#000000",
        background: "#ffffff",
        ecl: "M",
      });

      const svg = qr.svg();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      const pdf = new jsPDF("p", "mm", "a4");
      pdf.setFont("helvetica", "bold");

      img.onload = () => {
        canvas.width = qrSize;
        canvas.height = qrSize;
        ctx.drawImage(img, 0, 0, qrSize, qrSize);

        const imgData = canvas.toDataURL("image/jpeg", 1.0);
        const scaleFactor = 0.5;
        const scaledWidth = qrSize * scaleFactor;
        const scaledHeight = qrSize * scaleFactor;

        const pageWidth = pdf.internal.pageSize.getWidth();
        const initialX = (pageWidth - scaledWidth) / 2;
        const initialY = 25;

        // Add QR Code to PDF
        pdf.addImage(imgData, "JPEG", initialX, initialY, scaledWidth, scaledHeight);

        addQROverlay(pdf, vehicle, initialX, initialY, scaledWidth);

        addQRCodeVehicleDetails(pdf, vehicle, initialY + scaledHeight + 10);

        // Save the PDF into a Blob
        const pdfBlob = pdf.output("blob");

        // Add PDF file to the ZIP with a name reflecting table order
        zip.file(`${index + 1}_${vehicle.vehicleNumber}.pdf`, pdfBlob);

        resolve();
      };

      img.onerror = reject;
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    });
  });

  try {
    await Promise.all(promises);
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "qrcodes.zip");
    console.log("ZIP file with PDFs generated and downloaded successfully.");
  } catch (error) {
    console.error("Error generating or downloading ZIP file:", error);
  }
};

const getRoleId = () => {
  const roleData = JSON.parse(localStorage.getItem("rolePermissions"));
  return Array.isArray(roleData) ? roleData?.[0]?.roleId : undefined;
};

// for past dates
const formatDate = (date) => date.toISOString().split("T")[0];

export const getTodayDate = () => {
  const roleId = getRoleId();
  if (roleId === 1) return undefined;
  return formatDate(new Date());
};

export const getOneMonthPastDate = () => { 
  const roleId = getRoleId();
  if (roleId === 1) return undefined;

  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return formatDate(date);
};

// for past date times
const formatDateTime = (date) => date.toISOString().slice(0, 16);

export const getNowDateTime = () => {
  const roleId = getRoleId();
  if (roleId === 1) return undefined;
  return formatDateTime(new Date());
};

export const getOneMonthPastDateTime = () => {
  const roleId = getRoleId();
  if (roleId === 1) return undefined;

  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return formatDateTime(date);
};